import _ from 'lodash'
import { put, call, select } from 'redux-saga/effects'

import { createLegacyService } from 'core/service-wrapper/legacy/service'
import { createPartnerApiService } from 'core/service-wrapper/partner-api/service'
import * as eSubService from 'e-submission/domain/service'
import { isLoggedIn } from 'identity/selectors'
import {
  getProductsFailed,
  getProductsSuccess,
  partnerUpdateUseLegacyServiceWrapper,
  partnerUpdateUseLegacySubmissionWrapper,
  patchProductsSuccess,
  setViewConfiguration,
} from 'quick-quote/actions'
import * as insuredInformationSaga from 'quick-quote/insured-information/sagas'
import * as insuredInformationSelector from 'quick-quote/insured-information/selectors'
import * as investmentActions from 'quick-quote/product-investment/coverage-plan/actions'
import { availablePlanCode, availableProductCategories } from 'quick-quote/product-selection/actions'
import { isUseLegacyServiceForProduct, isUseLegacySubmission } from 'quick-quote/selectors/partner-selector'
import { store as qqStore } from 'store'

/**
 * loadProduct
 * @param {boolean} partnerApiAvailable
 * @param {LegacyServiceWrapper} legacy
 * @param {PartnerApiServiceWrapper} partner
 * @param {boolean} patchProduct
 */
function* loadProduct(partnerApiAvailable, legacy, partner, patchProduct = false) {
  try {
    const isLogin = yield select(isLoggedIn)
    const dbWrapper = legacy.getDBWrapper()
    const images = yield call(dbWrapper.getProductImages)
    const finalUpdateProduct = matchedProductImage(patchProduct ? patchProductsSuccess : getProductsSuccess)
    const legacyProducts = yield call(legacy.dump.loadProducts)
    if (partnerApiAvailable && isLogin) {
      const { viewConfiguration, availableCategories, availablePlanCodes, displayProducts } = yield call(
        partner.dump.loadProducts
      )
      const products = {}

      Array.from(displayProducts).forEach((product) => (products[product.code] = product))
      Array.from(legacyProducts).forEach((product) => {
        const mergeProduct = _.get(products, product.code, {})
        const excludeMergeFieldKeys = _.get(mergeProduct, 'excludeMergeFieldKeys', [])

        products[product.code] = {
          ..._.omit(mergeProduct, ['excludeMergeFieldKeys']),
          ..._.omit(product, excludeMergeFieldKeys),
        }
      })

      yield put(availableProductCategories([...availableCategories]))
      yield put(availablePlanCode([...availablePlanCodes]))
      yield put(setViewConfiguration(viewConfiguration))
      yield call(finalUpdateProduct, Object.values(products), images)
    } else {
      yield call(legacy.dump.loadViewConfiguration)
      yield call(finalUpdateProduct, legacyProducts, images)
    }
  } catch (error) {
    console.error(error)
    yield put(getProductsFailed(error))
  }
}

function matchedProductImage(actionUpdateProduct) {
  return function*(products, images) {
    for (const product of products) {
      const imagesBase64 = images.filter((m) => m.name === product.image)
      if (imagesBase64.length) {
        product.image = imagesBase64[0].b64
      }
    }

    yield put(actionUpdateProduct(products))
  }
}

/**
 *
 * @param partnerApiAvailable {boolean}
 * @param legacy {LegacyServiceWrapper}
 * @param partner {PartnerApiServiceWrapper}
 * @returns {Generator<*, (function(): void)|*, *>}
 */
function initial(partnerApiAvailable, legacy, partner) {
  return function*(localDbUrl) {
    const dbWrapper = legacy.getDBWrapper()

    yield call(dbWrapper.init, localDbUrl)

    if (partnerApiAvailable) {
      dbWrapper.list('rider-benefit').then((result) => partner.dump.updateRidersBenefits(result))
    }

    // prettier-ignore
    if (partnerApiAvailable) {
      yield call(partner.dump.loadFNAConfiguration)
      yield call(partner.dump.loadQuestionnaire)
      yield call(partner.dump.loadOccupations)
    } else {
      yield call(legacy.dump.loadFNAConfiguration)
      yield call(legacy.dump.loadQuestionnaire)
      yield call(legacy.dump.loadOccupations)
    }

    yield call(legacy.dump.loadDistributorGroups)
    yield call(legacy.dump.loadOccupationFactors)
    yield call(legacy.dump.loadModelFactors)
    yield call(loadProduct, partnerApiAvailable, legacy, partner)
    yield call(legacy.dump.loadRiders)
  }
}

/**
 * @param usePartnerApi {boolean}
 */
export async function buildServiceWrapper(usePartnerApi) {
  const legacy = await createLegacyService()
  const partner = usePartnerApi ? await createPartnerApiService(legacy) : undefined

  let useLegacyServiceForProduct = true

  function noop(...args) {}
  function notImplement(...args) {}

  /**
   * switchService
   * @param legacyFunc {function(...args): Generator}
   * @param partnerFunc {function(...args): Generator}
   * @param {{
   *   [beforeCall]: undefined | function(...args): void
   *   [shouldSkip]: undefined | function(...args): void
   *   [shouldUseLegacySubmission]: undefined | boolean
   * }} options
   * @return {function(...args): Generator | function(...args): void | function(...args): Promise}
   */
  function switchService(legacyFunc, partnerFunc, options = {}) {
    const { beforeCall, shouldSkip, shouldUseLegacySubmission } = options
    return function*(...args) {
      if (typeof shouldSkip === 'function') {
        const skipped = yield call(shouldSkip, ...args)
        if (skipped) return
      }
      if (typeof beforeCall === 'function') yield call(beforeCall, ...args)

      // support service wrapper for e-submission
      let useLegacyFunc
      if (typeof shouldUseLegacySubmission === 'boolean' && shouldUseLegacySubmission) {
        useLegacyFunc = yield select(isUseLegacySubmission)
      } else {
        useLegacyServiceForProduct = yield select(isUseLegacyServiceForProduct)
        useLegacyFunc = useLegacyServiceForProduct
      }

      if (useLegacyFunc) {
        return yield call(legacyFunc, ...args)
      } else {
        return yield call(partnerFunc, ...args)
      }
    }
  }

  /**
   * switchServiceNonYield
   * @param legacyFunc {function(...args): void | function(...args): Promise}
   * @param partnerFunc {function(...args): void | function(...args): Promise}
   */
  function switchServiceNonYield(legacyFunc, partnerFunc) {
    const handlerAsync = (promise) => {
      if (promise instanceof Promise) {
        return promise.then((result) => result)
      } else {
        return promise
      }
    }

    return (...args) => {
      useLegacyServiceForProduct = isUseLegacyServiceForProduct(qqStore.getState())

      if (useLegacyServiceForProduct) {
        return handlerAsync(legacyFunc(...args))
      } else {
        return handlerAsync(partnerFunc(...args))
      }
    }
  }

  function isProductUseLegacy(displayProduct) {
    return _.isNil(displayProduct.useLegacy) || displayProduct.useLegacy
  }

  // prettier-ignore
  return {
    usePartnerApi: usePartnerApi,
    initial: initial(usePartnerApi, legacy, partner),
    subscribeRouteChange: partner?.subscribeRouteChange,
    checkApplicationDataReady: legacy.checkApplicationDataReady(usePartnerApi),
    isCurrentProductUsePartnerApi: () => !useLegacyServiceForProduct,
    isProductUseLegacy,
    isOrdinaryForceLoginProduct: (displayProduct) => {
      if (_.isNil(displayProduct.useLegacy) || displayProduct.useLegacy) {
        return legacy.isOrdinaryForceLoginProduct(displayProduct)
      } else {
        return partner?.isOrdinaryForceLoginProduct(displayProduct)
      }
    },
    renderGraph: switchServiceNonYield(legacy.renderGraph, partner?.renderGraph),
    getBasicPlan: switchServiceNonYield(legacy.getBasicPlan, partner?.getBasicPlan),
    getRiskiestOccupation: switchServiceNonYield(legacy.getRiskiestOccupation, partner?.getRiskiestOccupation),
    getRiskiestOccupationFactorForRiders: switchServiceNonYield(
      legacy.getRiskiestOccupationFactorForRiders,
      partner?.getRiskiestOccupationFactorForRiders
    ),
    insured: {
      getOccupations: insuredInformationSelector.getUniqueOccupations,
      onEditBirthdate: switchService(legacy.insured.onEditBirthdate, partner?.insured.onEditBirthdate),
      onSelectOccupation: insuredInformationSaga.updateNatureOfDuty,
      onSelectOtherOccupation: insuredInformationSaga.updateOtherNatureOfDuty,
      onSelectNatureOfDuty: switchService(legacy.insured.onSelectNatureOfDuty, partner?.insured.onSelectNatureOfDuty),
      onSelectOtherNatureOfDuty: switchService(
        legacy.insured.onSelectOtherNatureOfDuty,
        partner?.insured.onSelectOtherNatureOfDuty
      ),
    },
    payer: {
      getOccupations: insuredInformationSelector.getUniqueOccupations,
      onEditBirthdate: switchService(legacy.payer.onEditBirthdate, partner?.payer.onEditBirthdate),
      onSelectOccupation: insuredInformationSaga.updatePayerNatureOfDuty,
      onSelectOtherOccupation: noop,
      onSelectNatureOfDuty: switchService(legacy.payer.onSelectNatureOfDuty, partner?.payer.onSelectNatureOfDuty),
      onSelectOtherNatureOfDuty: switchService(
        legacy.payer.onSelectOtherNatureOfDuty,
        partner?.payer.onSelectOtherNatureOfDuty
      ),
      onUpdatePayerNeed: switchService(noop, partner?.payer.onUpdatePayerNeed),
    },
    products: {
      BUMNAN_READY: {
        getSelectableProductBumnanReady: switchServiceNonYield(
          legacy.products.BUMNAN_READY.getSelectableProductBumnanReady,
          partner?.products.BUMNAN_READY.getSelectableProductBumnanReady
        ),
        onSelectProduct: switchService(
          legacy.products.BUMNAN_READY.onSelectProduct,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
        switchBumnanReadyProductByAge: switchService(
          legacy.products.BUMNAN_READY.handleUpdateBirthDateWithProductBumnan,
          noop
        ),
        switchBumnanSmartProductByPensionAge: switchService(
          legacy.products.BUMNAN_READY.switchBumnanSmartProductByPensionAge,
          notImplement
        ),
        switchKoomverRetireProductByPensionAge: switchService(
          legacy.products.BUMNAN_READY.switchKoomverRetireProductByPensionAge,
          notImplement
        ),
        switchBumnanSmartProduct: switchService(legacy.products.BUMNAN_READY.switchBumnanSmartProduct, notImplement),
      },
      COMMON: {
        updateProducts: function*() {
          const dbWrapper = legacy.getDBWrapper()
          if (!dbWrapper.isReady()) {
            return
          }

          yield call(loadProduct, usePartnerApi, legacy, partner, true)
        },
        onSelectProduct: switchService(
          legacy.products.COMMON.onSelectProduct,
          partner?.products.COMMON.onSelectProduct,
          {
            beforeCall: function*(displayProduct) {
              useLegacyServiceForProduct = isProductUseLegacy(displayProduct)
              yield put(partnerUpdateUseLegacyServiceWrapper(useLegacyServiceForProduct))
            },
          }
        ),
        onSelectModelFactor: switchService(noop, partner?.products.COMMON.onSelectModelFactor),
        onCalculateFromSumAssuredChange: switchService(
          legacy.products.COMMON.generateCoveragePlanFromSumAssuredChange,
          partner?.products.COMMON.onCalculateFromSumAssuredChange
        ),
        onCalculateFromPremiumChange: switchService(
          legacy.products.COMMON.generateCoveragePlanFromPremiumChange,
          partner?.products.COMMON.onCalculateFromPremiumChange
        ),
        onToggleRider: switchService(
          legacy.products.COMMON.updateRiderSelectedPlan,
          partner?.products.COMMON.onToggleRider
        ),
        onEditRiderSelectPlan: switchService(
          legacy.products.COMMON.updateDefaultRiderSumAssured,
          partner?.products.COMMON.onEditRiderSelectPlan
        ),
        onCalculateFromRiderSumAssuredChange: switchService(
          legacy.products.COMMON.updateRiderPremium,
          partner?.products.COMMON.onCalculateFromRiderSumAssuredChange
        ),
        onCalculateFromRiderPremiumChange: switchService(
          legacy.products.COMMON.revalidateRiders,
          partner?.products.COMMON.onCalculateFromRiderPremiumChange
        ),
        onSelectPensionType: switchService(noop, partner?.products.COMMON.onSelectPensionType),
        // sale proposal
        prepareBIProps: switchService(legacy.products.COMMON.prepareBIProps, partner?.products.COMMON.prepareBIProps),
        generatePDFToBase64: switchServiceNonYield(
          legacy.products.COMMON.generatePDFToBase64,
          partner?.products.COMMON.generatePDFToBase64
        ),
        // ra, rpq form
        generateRaAndRpqForm: switchService(
          legacy.products.COMMON.generateRaAndRpqForm,
          partner?.products.COMMON.generateRaAndRpqForm
        ),
        // support legacy
        onCalculatePremium: switchService(
          legacy.products.COMMON.onCalculatePremium,
          partner?.products.COMMON.updateProductPackage
        ),
        updateTotalPremiumState: switchService(legacy.products.COMMON.updateTotalPremiumState, noop),
        updateAllSelectedRiderPremium: switchService(legacy.products.COMMON.updateAllSelectedRiderPremium, noop),
        revalidateRiders: switchService(legacy.products.COMMON.revalidateRiders, noop),
        populateAvailableRiders: switchService(legacy.products.COMMON.populateAvailableRiders, noop),

        isUsedTMORate: switchServiceNonYield(legacy.products.COMMON.isUsedTMORate, () => true),
      },
      HEALTH: {
        onUpdateSelectedBasicPlan: switchService(
          legacy.products.HEALTH.onUpdateSelectedBasicPlan,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
        onValidateBasicPlan: switchService(legacy.products.HEALTH.onValidateBasicPlan, notImplement),
      },
      HEALTH_TOPPING: {
        onUpdateSelectedBasicPlan: switchService(
          legacy.products.HEALTH_TOPPING.onUpdateSelectedBasicPlan,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
      },
      I_HEALTHY_ULTRA: {
        onUpdateSelectedBasicPlan: switchService(
          legacy.products.I_HEALTHY_ULTRA.onUpdateSelectedBasicPlan,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
        getBasicPlanOptions: switchServiceNonYield(
          legacy.products.I_HEALTHY_ULTRA.getBasicPlanOptions,
          partner?.products.I_HEALTHY_ULTRA.getBasicPlanOptions
        ),
        getSelectedDisplayProductCodeIHealthyUltra: switchServiceNonYield(
          legacy.products.I_HEALTHY_ULTRA.getSelectedDisplayProductCodeIHealthyUltra,
          partner?.products.I_HEALTHY_ULTRA.getSelectedDisplayProductCodeIHealthyUltra
        ),
        // support legacy
        switchIHealthyProductByBirthDate: switchService(
          legacy.products.I_HEALTHY_ULTRA.switchIHealthyProductByBirthDate,
          noop
        ),
        onSelectBasicPlan: switchService(legacy.products.I_HEALTHY_ULTRA.onSelectBasicPlan, noop),
        validateProductMultiBasicPlanCode: switchService(
          legacy.products.I_HEALTHY_ULTRA.validateProductMultiBasicPlanCode,
          noop
        ),
        generateCoveragePlanFromBasicPlanChange: switchService(
          legacy.products.I_HEALTHY_ULTRA.generateCoveragePlanFromBasicPlanChange,
          noop
        ),
        onValidateBasicPlan: switchService(legacy.products.I_HEALTHY_ULTRA.onValidateBasicPlan, noop),
      },
      INVESTMENT: {
        // benefit
        isUsedCoiTMORate: switchServiceNonYield(legacy.products.INVESTMENT.isUsedCoiTMORate, () => true),
        addOrRemoveRiderBenefits: switchService(legacy.products.INVESTMENT.addOrRemoveRiderBenefits, noop),
        // coverage
        onSelectedModalFactor: switchService(noop, partner?.products.INVESTMENT.onSelectedModalFactor),
        onEditSumAssured: switchService(
          legacy.products.INVESTMENT.onEditSumAssured,
          partner?.products.INVESTMENT.onEditSumAssured,
          {
            shouldSkip: function*(action) {
              const useLegacyServiceForProduct = yield select(isUseLegacyServiceForProduct)
              if (useLegacyServiceForProduct) {
                return false
              }
              return action.type !== investmentActions.EDIT_SUM_ASSURED
            },
          }
        ),
        onEditRegularPremium: switchService(noop, partner?.products.INVESTMENT.onEditRegularPremium),
        onEditRegularTopUp: switchService(
          legacy.products.INVESTMENT.onEditRegularTopUp,
          partner?.products.INVESTMENT.onEditRegularTopUp,
          {
            shouldSkip: function*(action) {
              const useLegacyServiceForProduct = yield select(isUseLegacyServiceForProduct)
              if (useLegacyServiceForProduct) {
                return false
              }
              return action.type !== investmentActions.EDIT_REGULAR_TOP_UP
            },
          }
        ),
        onEditLumpSum: switchService(
          legacy.products.INVESTMENT.onEditLumpSum,
          partner?.products.INVESTMENT.onEditLumpSum
        ),
        onUpdateRegularPremium: switchService(legacy.products.INVESTMENT.onUpdateRegularPremium, noop),
        doUpdateExpectedReturnComparisons: switchService(
          legacy.products.INVESTMENT.doUpdateExpectedReturnComparisons,
          noop
        ),
        revalidateSumAssured: switchService(legacy.products.INVESTMENT.revalidateSumAssured, noop),
        // fund
        getSellableFunds: switchService(
          legacy.products.INVESTMENT.getSellableFunds,
          partner?.products.INVESTMENT.getSellableFunds
        ),
        getFundLimit: switchServiceNonYield(
          legacy.products.INVESTMENT.getFundLimit,
          partner?.products.INVESTMENT.getFundLimit
        ),
        getCashFlowProps: switchService(legacy.products.INVESTMENT.getCashFlowProps, noop),
        generateCashFlow: switchServiceNonYield(legacy.products.INVESTMENT.generateCashFlow, noop),
        generateCashFlowByExpectedReturn: switchServiceNonYield(
          legacy.products.INVESTMENT.generateCashFlowByExpectedReturn,
          noop
        ),
      },
      LIFE_READY: {
        onUpdateSelectedLifeReady: switchService(
          legacy.products.LIFE_READY.onUpdateSelectedLifeReady,
          partner?.products.LIFE_READY.onUpdateSelectedLifeReady
        ),
        onUpdateSelectedLifeTreasure: switchService(
          legacy.products.LIFE_READY.onUpdateSelectedLifeTreasure,
          partner?.products.LIFE_READY.onUpdateSelectedLifeTreasure
        ),
        onUpdateSelectedCancerX5: switchService(
          legacy.products.LIFE_READY.onUpdateSelectedCancerX5,
          partner?.products.LIFE_READY.onUpdateSelectedCancerX5
        ),
        onUpdateSelectedKoomverCover: switchService(
          legacy.products.LIFE_READY.onUpdateSelectedKoomverCover,
          partner?.products.LIFE_READY.onUpdateSelectedKoomverCover
        ),
        onUpdateSelectKoomverCompensate: switchService(
          legacy.products.LIFE_READY.onUpdateSelectKoomverCompensate,
          partner?.products.LIFE_READY.onUpdateSelectKoomverCompensate
        ),
        switchProductBySumAssured: switchService(legacy.products.LIFE_READY.switchProductBySumAssured, noop),
      },
      LIFE_SAVE_PRO: {
        switchLifeSaveProProductByBirthDate: switchService(
          legacy.products.LIFE_SAVE_PRO.switchLifeSaveProProductByBirthDate,
          noop
        ),
        calculateInvestmentBenefit: switchService(legacy.products.LIFE_SAVE_PRO.calculateInvestmentBenefit, noop),
      },
      LOAN: {
        validateAndUpdateLoanAmount: switchService(legacy.products.LOAN.validateAndUpdateLoanAmount, notImplement),
        validateAndUpdateLoanTerm: switchService(legacy.products.LOAN.validateAndUpdateLoanTerm, notImplement),
        doValidateSumAssured: switchService(legacy.products.LOAN.doValidateSumAssured, notImplement),
        doValidateCoverageTerm: switchService(legacy.products.LOAN.doValidateCoverageTerm, notImplement),
        onUpdateInsuranceLoanTerm: switchService(legacy.products.LOAN.onUpdateInsuranceLoanTerm, notImplement),
        setProductDefaults: switchService(legacy.products.LOAN.setProductDefaults, notImplement),
        onCalculatePremium: switchService(legacy.products.LOAN.onCalculatePremium, notImplement),
        onGetDocumentListRequest: switchService(legacy.products.LOAN.onGetDocumentListRequest, notImplement),
      },
      MRTA: {
        setProductDefaultsMRTA: switchService(legacy.products.MRTA.setProductDefaultsMRTA, notImplement),
        selectProductMRTA: switchService(
          legacy.products.MRTA.selectProductMRTA,
          partner?.products.MRTA.selectProductMRTA
        ),
        generateCoveragePlanFromLoanAmountChange: switchService(
          legacy.products.MRTA.generateCoveragePlanFromLoanAmountChange,
          partner?.products.MRTA.onEditLoanAmount
        ),
        validateCoveragePeriodChange: switchService(
          legacy.products.MRTA.validateCoveragePeriodChange,
          partner?.products.MRTA.validateCoveragePeriodChange
        ),
        validateLoanPeriodChange: switchService(
          legacy.products.MRTA.validateLoanPeriodChange,
          partner?.products.MRTA.validateLoanPeriodChange
        ),
        validateInterestRateErrors: switchService(
          legacy.products.MRTA.validateInterestRateErrors,
          partner?.products.MRTA.validateInterestRateErrors
        ),
        resetInterestRate: switchService(
          legacy.products.MRTA.resetInterestRate,
          partner?.products.MRTA.resetInterestRate
        ),
        switchPlanMRTAProductByAge: switchService(legacy.products.MRTA.switchPlanMRTAProductByAge, notImplement),
      },
      PA: {
        setProductDefaultsPA: switchService(legacy.products.PA.setProductDefaultsPA, noop),
        getAvailableOptionPlanPARider: switchServiceNonYield(
          legacy.products.PA.getAvailableOptionPlanPARider,
          partner?.products.PA.getAvailableOptionPlanPARider
        ),
        resolveRiderOptionForPlan: switchServiceNonYield(
          legacy.products.PA.resolveRiderOptionForPlan,
          partner?.products.PA.resolveRiderOptionForPlan
        ),
      },
      PERFECT_LIFE: {
        switchProductByBirthDate: switchService(legacy.products.PERFECT_LIFE.switchProductByBirthDate, noop),
        onUpdateSelectedBasicPlan: switchService(legacy.products.PERFECT_LIFE.selectPerfectLifePlan, noop),
      },
      SAVING: {
        onUpdateSelectedBasicPlan: switchService(legacy.products.COMMON.generateCoveragePlanFromSumAssuredChange, noop),
      },
      SUKHAPABDEKDEE: {
        onUpdateSelectedBasicPlan: switchService(
          legacy.products.SUKHAPABDEKDEE.selectSukhapabDekDeePlan,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
      },
      SIO: {
        onUpdateSelectedProductSIOWithCombineBoxPlanCode: switchService(
          legacy.products.SIO.onUpdateSelectedProductSIOWithCombineBoxPlanCode,
          notImplement
        ),
        switchProductSIOBySumAssured: switchService(legacy.products.SIO.switchProductSIOBySumAssured, notImplement),
        switchPlanProductSIOByAge: switchService(legacy.products.SIO.switchPlanProductSIOByAge, notImplement),
      },
      TERM_LIFE: {
        onUpdateSelectedTermLife: switchService(
          legacy.products.TERM_LIFE.onUpdateSelectedTermLife,
          partner?.products.COMMON.onUpdateSelectedBasicPlan
        ),
        switchTermLifeGroupProductByAge: switchService(legacy.products.TERM_LIFE.switchTermLifeGroupProductByAge, noop),
        getSelectableProductUltimateGrowth: switchServiceNonYield(
          legacy.products.TERM_LIFE.getSelectableProductUltimateGrowth,
          partner?.products.TERM_LIFE.getSelectableProductUltimateGrowth
        ),
        calculateInvestmentBenefit: switchService(legacy.products.TERM_LIFE.calculateInvestmentBenefit, noop),
      },
      WHOLE_LIFE: {
        switchProductByBirthDate: switchService(legacy.products.WHOLE_LIFE.switchProductByBirthDate, noop),
        addOrRemoveRiderBenefits: switchService(legacy.products.WHOLE_LIFE.addOrRemoveRiderBenefits, noop),
        generatePolicyValue: switchService(legacy.products.WHOLE_LIFE.generatePolicyValue, noop),
      },
      LIFE_PROTECT_PLUS: {
        onUpdateSwitchPlanLifeProtectPlus: switchService(
          noop,
          partner?.products.LIFE_PROTECT_PLUS.onUpdateSwitchPlanLifeProtectPlus
        ),
        onUpdateSelectedBoosterPlan: switchService(noop, partner?.products.LIFE_PROTECT_PLUS.onUpdateSelectedBoosterPlan),
      },
    },
    e_submission: {
      onLoadApp: function*(app, isNewApp) {
        let useLegacySubmission
        const useQuickQuotePartnerApi = _.get(app, 'quickQuote.biProps.usePartnerApi', false)
        if (useQuickQuotePartnerApi && !_.isNil(_.get(app, 'quickQuote.selectedDisplayProduct.useLegacySubmission'))) {
          useLegacySubmission = _.get(app, 'quickQuote.selectedDisplayProduct.useLegacySubmission')
        } else {
          useLegacySubmission = true
        }
        yield put(partnerUpdateUseLegacyServiceWrapper(!useQuickQuotePartnerApi))
        yield put(partnerUpdateUseLegacySubmissionWrapper(useLegacySubmission))
      },
      product_form: {
        investment: {
          // KYCSection: switchServiceNonYield(legacy.e_submission.product_form.investment.KYCSection, partner?.e_submission.product_form.investment.KYCSection),
          KYCSection: legacy.e_submission.product_form.investment.KYCSection,
        },
      },
      policy: {
        generatePdfProps: switchServiceNonYield(noop, partner?.e_submission.policy.generatePdfProps),
        createPolicy: switchService(eSubService.createPolicy, partner?.e_submission.policy.createPolicy, { shouldUseLegacySubmission: true }),
        updatePolicy: switchService(eSubService.updatePolicy, partner?.e_submission.policy.updatePolicy, { shouldUseLegacySubmission: true }),
        patchPolicy: eSubService.patchPolicy,
      },
    },
  }
}
